/**
 * SummariesCard — renders the 4-cell summary grid (2 users × 2 locales)
 * for a single match.
 *
 * Each cell renders either:
 *   - the stored summary with overall score + 6 PillarRow accordions, OR
 *   - a "Not generated yet" placeholder, OR
 *   - a "Stale" banner when current evidence count exceeds the snapshot.
 *
 * Permission gates:
 *   - viewing requires `matches.summaries.read` (caller pre-checks)
 *   - the embedded RegenerateMenu is gated on `matches.summary.regenerate`
 *
 * Backend contract:
 *   GET /v1/admin/matches/:matchId/summaries?locale=both|en|es
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2 / §6.3
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { PillarRow, type PillarShape } from './PillarRow';
import { PillarRatingWidget } from './PillarRatingWidget';
import { RegenerateMenu } from './RegenerateMenu';

interface UserRef {
  id: string;
  display_name?: string | null;
  email?: string | null;
}

interface DealbreakerCheck {
  category: string;
  passed: boolean;
}

interface SummaryCell {
  for_user_id: string;
  locale: 'en' | 'es';
  summary: {
    pillars: PillarShape[];
    overall_compatibility: number;
    compatibility_tier: string;
    evidence_count_at_generation: number | null;
    current_evidence_count: number;
    is_stale: boolean;
    generated_at: string;
    model_id: string | null;
    // Overview tab (mirrors mobile OverviewTabContent)
    match_rationale?: string | null;
    top_strengths?: string[] | null;
    what_makes_promising?: string | null;
    overall_summary?: string | null;
    conversation_igniter?: string | null;
    contradiction_class?: string | null;
    // More tab (mirrors mobile MoreTabContent)
    attraction_alignment?: string | null;
    best_for?: string | null;
    confidence_score?: string | null;
    confidence_basis?: string | null;
    dealbreakers_checked?: DealbreakerCheck[] | null;
  } | null;
}

interface SummariesResponse {
  summaries: SummaryCell[];
}

interface Props {
  matchId: string;
  match: { user1: UserRef; user2: UserRef };
}

type LocaleFilter = 'en' | 'es' | 'both';

export function SummariesCard({ matchId, match }: Props) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [data, setData] = useState<SummariesResponse | null>(null);
  const [locale, setLocale] = useState<LocaleFilter>('both');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminFetch(
        `${apiUrl}/v1/admin/matches/${matchId}/summaries?locale=${locale}`,
      );
      const json = (await r.json()) as SummariesResponse;
      setData(json);
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Failed to load summaries');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, matchId, locale]);

  useEffect(() => {
    reload();
  }, [reload]);

  const u1Cells = data?.summaries.filter(s => s.for_user_id === match.user1.id) ?? [];
  const u2Cells = data?.summaries.filter(s => s.for_user_id === match.user2.id) ?? [];

  const u1Label = userLabel(match.user1);
  const u2Label = userLabel(match.user2);

  return (
    <section className="mt-4 rounded-md border bg-card p-3 text-card-foreground">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>6-pillar summaries</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="text-xs text-muted-foreground">Locale:</span>
          {(['en', 'es', 'both'] as const).map(l => (
            <button
              key={l}
              type="button"
              onClick={() => setLocale(l)}
              aria-pressed={locale === l}
              className={
                locale === l
                  ? 'cursor-pointer rounded bg-primary px-2.5 py-1 text-xs text-primary-foreground'
                  : 'cursor-pointer rounded border bg-background px-2.5 py-1 text-xs text-foreground hover:bg-muted'
              }
            >
              {l}
            </button>
          ))}
          {hasPermission('matches.summary.regenerate') && (
            <RegenerateMenu matchId={matchId} match={match} onDone={reload} />
          )}
        </div>
      </div>

      {err && (
        <div className="mb-2 rounded bg-destructive/10 p-2 text-[13px] text-destructive">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-muted-foreground">Loading summaries…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <h4 className={columnTitleClass}>
              {u1Label}'s view of {u2Label}
            </h4>
            {u1Cells.length === 0 && (
              <p className={emptyTextClass}>No summary for this locale.</p>
            )}
            {u1Cells.map(s => (
              <SummaryCellView
                key={`${s.for_user_id}-${s.locale}`}
                cell={s}
                matchId={matchId}
                user1Label={u1Label}
                user2Label={u2Label}
              />
            ))}
          </div>
          <div>
            <h4 className={columnTitleClass}>
              {u2Label}'s view of {u1Label}
            </h4>
            {u2Cells.length === 0 && (
              <p className={emptyTextClass}>No summary for this locale.</p>
            )}
            {u2Cells.map(s => (
              <SummaryCellView
                key={`${s.for_user_id}-${s.locale}`}
                cell={s}
                matchId={matchId}
                user1Label={u1Label}
                user2Label={u2Label}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SummaryCellView({
  cell,
  matchId,
  user1Label,
  user2Label,
}: {
  cell: SummaryCell;
  matchId: string;
  user1Label: string;
  user2Label: string;
}) {
  const { hasPermission } = useAdminPermissions();
  const { summary, locale, for_user_id } = cell;
  // Per-cell tab — mirrors the mobile match-detail Breakdown/Overview/More
  // navigator so the admin reviews the summary exactly as the user sees it.
  const [tab, setTab] = useState<SummaryTab>('breakdown');

  if (!summary) {
    return (
      <div className="mt-1.5 rounded-md border bg-muted/50 p-2.5 text-xs">
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          Locale: <code>{locale}</code>
        </div>
        <div className="text-muted-foreground">
          Not generated yet.
          {hasPermission('matches.summary.regenerate') && (
            <span> Use the Regenerate menu above to create one.</span>
          )}
        </div>
      </div>
    );
  }

  const overallColorClass = scoreColorClass(summary.overall_compatibility);

  return (
    <div className="mt-1.5 rounded-md border bg-card p-2.5 text-[13px] text-card-foreground">
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
        <span style={{ fontWeight: 600 }}>
          Locale: <code>{locale}</code>
        </span>
        <span className="text-[11px] text-muted-foreground">
          {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : '—'}
        </span>
      </div>

      <div style={{ marginTop: 6 }}>
        Overall:{' '}
        <strong className={`text-base ${overallColorClass}`}>
          {summary.overall_compatibility}
        </strong>{' '}
        <span className="text-xs text-muted-foreground">
          ({summary.compatibility_tier})
        </span>
      </div>

      <div className="mt-1 text-[11px] text-muted-foreground">
        Evidence: {summary.evidence_count_at_generation ?? 0} → {summary.current_evidence_count}
        {summary.is_stale && (
          <span className="ml-1.5 font-semibold text-amber-500">
            ⚠ stale (+{summary.current_evidence_count - (summary.evidence_count_at_generation ?? 0)})
          </span>
        )}
        {summary.model_id && (
          <span style={{ marginLeft: 6 }}>· model: {summary.model_id}</span>
        )}
      </div>

      {hasPermission('matches.summary.rate') && (
        <PillarRatingWidget
          matchId={matchId}
          forUserId={for_user_id}
          locale={locale}
          pillarIndex={-1}
        />
      )}

      {/* Breakdown / Overview / More — same three sections the user sees on
          the mobile match-detail screen. Defaults to Breakdown. */}
      <div className="mt-2.5 flex gap-1" role="tablist" aria-label="Summary sections">
        {SUMMARY_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={
              tab === t.key
                ? 'cursor-pointer rounded bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground'
                : 'cursor-pointer rounded border bg-background px-2.5 py-1 text-xs text-foreground hover:bg-muted'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'breakdown' && (
        <div style={{ marginTop: 8 }}>
          {(summary.pillars ?? []).map((p, idx) => (
            <PillarRow
              key={idx}
              pillar={p}
              pillarIndex={idx}
              matchId={matchId}
              forUserId={for_user_id}
              locale={locale}
              user1Label={user1Label}
              user2Label={user2Label}
            />
          ))}
        </div>
      )}

      {tab === 'overview' && <OverviewSection summary={summary} />}
      {tab === 'more' && <MoreSection summary={summary} />}
    </div>
  );
}

type SummaryTab = 'breakdown' | 'overview' | 'more';

const SUMMARY_TABS: ReadonlyArray<{ key: SummaryTab; label: string }> = [
  { key: 'breakdown', label: 'Breakdown' },
  { key: 'overview', label: 'Overview' },
  { key: 'more', label: 'More' },
];

type SummaryData = NonNullable<SummaryCell['summary']>;

/** Section heading shared by Overview + More. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground first:mt-2">
      {children}
    </div>
  );
}

/** Map a stored level string ('high'/'medium'/'low'/...) to a themed pill. */
function levelPillClass(value: string | null | undefined): string {
  const v = (value || '').toLowerCase();
  if (v.includes('high') || v.includes('strong')) return 'bg-green-500/15 text-green-500';
  if (v.includes('medium') || v.includes('moderate')) return 'bg-amber-500/15 text-amber-500';
  if (v.includes('low') || v.includes('weak')) return 'bg-red-500/15 text-red-500';
  return 'bg-muted text-muted-foreground';
}

function LevelPill({ value }: { value: string }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold capitalize ${levelPillClass(value)}`}>
      {value}
    </span>
  );
}

/**
 * Overview tab — match_rationale, top_strengths, what_makes_promising,
 * conversation_igniter, overall_summary (collapsible). Mirrors mobile
 * OverviewTabContent. All prose is name-hydrated server-side.
 */
function OverviewSection({ summary }: { summary: SummaryData }) {
  const [showFull, setShowFull] = useState(false);
  const hasAny =
    summary.match_rationale ||
    (summary.top_strengths && summary.top_strengths.length > 0) ||
    summary.what_makes_promising ||
    summary.conversation_igniter ||
    summary.overall_summary;

  if (!hasAny) {
    return <p className="mt-3 text-xs italic text-muted-foreground">No overview content in this summary.</p>;
  }

  return (
    <div className="mt-1 text-[13px] leading-relaxed text-foreground">
      {summary.contradiction_class && (
        <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-[12px] text-amber-600 dark:text-amber-400">
          Profile-consistency flag: <code>{summary.contradiction_class}</code>
        </div>
      )}

      {summary.match_rationale && (
        <>
          <SectionLabel>Why You Match</SectionLabel>
          <p className="text-muted-foreground">{summary.match_rationale}</p>
        </>
      )}

      {summary.top_strengths && summary.top_strengths.length > 0 && (
        <>
          <SectionLabel>Top Strengths</SectionLabel>
          <ul className="ml-0 list-none space-y-1">
            {summary.top_strengths.map((s, i) => (
              <li key={i} className="flex gap-2 text-muted-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" aria-hidden />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {summary.what_makes_promising && (
        <>
          <SectionLabel>What Makes This Promising</SectionLabel>
          <p className="text-muted-foreground">{summary.what_makes_promising}</p>
        </>
      )}

      {summary.conversation_igniter && (
        <>
          <SectionLabel>Conversation Starter</SectionLabel>
          <p className="border-l-2 border-primary pl-2.5 italic text-foreground">
            “{summary.conversation_igniter}”
          </p>
        </>
      )}

      {summary.overall_summary && (
        <>
          <button
            type="button"
            onClick={() => setShowFull(v => !v)}
            className="mt-3 cursor-pointer rounded border bg-background px-2.5 py-1 text-xs text-foreground hover:bg-muted"
            aria-expanded={showFull}
          >
            {showFull ? '▾ Hide full match report' : '▸ Full match report'}
          </button>
          {showFull && (
            <p className="mt-2 whitespace-pre-line rounded bg-muted/40 p-2.5 text-muted-foreground">
              {summary.overall_summary}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * More tab — attraction_alignment, best_for, confidence (+ basis),
 * dealbreakers_checked. Mirrors mobile MoreTabContent.
 */
function MoreSection({ summary }: { summary: SummaryData }) {
  const dealbreakers = summary.dealbreakers_checked ?? [];
  const hasAny =
    summary.attraction_alignment ||
    summary.best_for ||
    summary.confidence_score ||
    dealbreakers.length > 0;

  if (!hasAny) {
    return <p className="mt-3 text-xs italic text-muted-foreground">No additional detail in this summary.</p>;
  }

  return (
    <div className="mt-1 text-[13px] leading-relaxed text-foreground">
      {summary.attraction_alignment && (
        <>
          <SectionLabel>Attraction Alignment</SectionLabel>
          <LevelPill value={summary.attraction_alignment} />
        </>
      )}

      {summary.best_for && (
        <>
          <SectionLabel>Best For</SectionLabel>
          <p className="font-medium text-foreground">{summary.best_for}</p>
        </>
      )}

      {summary.confidence_score && (
        <>
          <SectionLabel>Confidence</SectionLabel>
          <LevelPill value={summary.confidence_score} />
          {summary.confidence_basis && (
            <p className="mt-1 text-muted-foreground">{summary.confidence_basis}</p>
          )}
        </>
      )}

      {dealbreakers.length > 0 && (
        <>
          <SectionLabel>Dealbreaker Check</SectionLabel>
          <div className="space-y-1">
            {dealbreakers.map((db, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 rounded border px-2 py-1 ${
                  db.passed ? 'border-green-500/30' : 'border-red-500/30'
                }`}
              >
                <span className={db.passed ? 'text-green-500' : 'text-red-500'} aria-hidden>
                  {db.passed ? '✓' : '✗'}
                </span>
                <span className="flex-1 font-medium capitalize text-foreground">{db.category}</span>
                <span
                  className={`text-[10px] font-bold uppercase ${
                    db.passed ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {db.passed ? 'Passed' : 'Flag'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function userLabel(u: UserRef) {
  return u.display_name || u.email || u.id.slice(0, 8);
}

function scoreColorClass(s: number): string {
  if (s >= 85) return 'text-green-500';
  if (s >= 65) return 'text-amber-500';
  return 'text-red-500';
}

const columnTitleClass =
  'mb-1.5 mt-0 text-[13px] uppercase tracking-wide text-muted-foreground';

const emptyTextClass = 'text-xs italic text-muted-foreground';
